/**
 * @fileoverview Human-readable labels for the 12 RelativeDateTokens — presentation
 *             layer only (the domain resolution lives in @firesquirrel/core-domain).
 * @module ui-admin/components/ParameterEditor/relativeDateTokens
 *
 * Responsibilities: - Provide ordered, labelled options for token pickers in the
 *                     ParameterEditor, ParameterPromptModal preset chips, and
 *                     ParameterBindingSection.
 * Dependencies:     - @firesquirrel/core-domain (RelativeDateToken type only).
 * Architecture:     - Pure static data; labels are a UI concern so they live here
 *                     rather than in the domain package.
 * Notes:            - Order matches common cadence (day → week → month → quarter → year).
 */

import type { RelativeDateToken } from '@firesquirrel/core-domain';

/**
 * @contract RelativeDateTokenOption
 * Purpose: One labelled token option for a select/chip list.
 * Fields:  token — the persisted RelativeDateToken; label — display text.
 */
export interface RelativeDateTokenOption {
  token: RelativeDateToken;
  label: string;
}

/**
 * @contract RELATIVE_DATE_TOKEN_OPTIONS
 * Purpose: Ordered labelled options for all 12 relative-date tokens.
 * Rules:   Order is presentation order (day → week → month → quarter → year).
 */
export const RELATIVE_DATE_TOKEN_OPTIONS: RelativeDateTokenOption[] = [
  { token: 'previousDay', label: 'Previous Day (yesterday)' },
  { token: 'currentDay', label: 'Current Day (today)' },
  { token: 'last7Days', label: 'Last 7 Days' },
  { token: 'last30Days', label: 'Last 30 Days' },
  { token: 'previousMonth', label: 'Previous Month' },
  { token: 'currentMonth', label: 'Current Month' },
  { token: 'nextMonth', label: 'Next Month' },
  { token: 'previousQuarter', label: 'Previous Quarter' },
  { token: 'currentQuarter', label: 'Current Quarter' },
  { token: 'yearToDate', label: 'Year to Date' },
  { token: 'previousYear', label: 'Previous Year' },
  { token: 'currentYear', label: 'Current Year' },
];

/**
 * @reusable relativeTokenLabel
 * Purpose: Look up the display label for a token.
 * Rules:   Pure function; returns the token itself when unknown.
 */
export function relativeTokenLabel(token: RelativeDateToken): string {
  return RELATIVE_DATE_TOKEN_OPTIONS.find((o) => o.token === token)?.label ?? token;
}
